import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, orderBy, getDocs, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { format } from 'date-fns';

// Navigation component
import Navbar from '../components/Navbar';

// Canadian provinces and territories data
const provinceNames: { [key: string]: string } = {
  'AB': 'Alberta',
  'BC': 'British Columbia',
  'MB': 'Manitoba',
  'NB': 'New Brunswick',
  'NL': 'Newfoundland and Labrador',
  'NS': 'Nova Scotia',
  'NT': 'Northwest Territories',
  'NU': 'Nunavut',
  'ON': 'Ontario',
  'PE': 'Prince Edward Island',
  'QC': 'Quebec',
  'SK': 'Saskatchewan',
  'YT': 'Yukon'
};

const MilesLogging = () => {
  const { currentUser, userDetails } = useAuth();
  
  // State for miles logging
  const [miles, setMiles] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // State for tracking progress
  const [userTotalMiles, setUserTotalMiles] = useState<number>(0);
  const [teamTotalMiles, setTeamTotalMiles] = useState<number>(0);
  const [teamName, setTeamName] = useState<string>('');
  const [provinceTotalMiles, setProvinceTotalMiles] = useState<number>(0);
  const [overallProgress, setOverallProgress] = useState<number>(0);
  
  // Recent logs
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  
  // Get user's total miles and team information
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser || !userDetails) return;
      
      try {
        // Get user's total miles
        const milesRef = collection(db, 'miles');
        const userMilesQuery = query(
          milesRef,
          where('userId', '==', currentUser.uid),
          orderBy('date', 'desc')
        );
        const milesSnapshot = await getDocs(userMilesQuery);
        
        let total = 0;
        const recentMilesLogs: any[] = [];
        milesSnapshot.forEach((doc) => {
          const data = doc.data();
          total += Number(data.miles);
          if (recentMilesLogs.length < 5) {
            recentMilesLogs.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        setUserTotalMiles(total);
        setRecentLogs(recentMilesLogs);
        
        // Get team information if user is in a team
        if (userDetails.teamId) {
          const teamDoc = await getDoc(doc(db, 'teams', userDetails.teamId));
          if (teamDoc.exists()) {
            const teamData = teamDoc.data();
            setTeamName(teamData.name);
            setTeamTotalMiles(teamData.totalMiles || 0);
          }
        }
        
        // Get province total miles
        const provinceDoc = await getDoc(doc(db, 'provinces', userDetails.province));
        if (provinceDoc.exists()) {
          const provinceData = provinceDoc.data();
          setProvinceTotalMiles(provinceData.totalMiles || 0);
        }
        
        // Get overall progress
        const totalMilesDoc = await getDoc(doc(db, 'statistics', 'totalMiles'));
        if (totalMilesDoc.exists()) {
          const totalData = totalMilesDoc.data();
          setOverallProgress(totalData.miles || 0);
        }
        
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [currentUser, userDetails]);
  
  // Submit miles
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!miles || isNaN(Number(miles)) || Number(miles) <= 0) {
      setError('Please enter a valid number of miles');
      return;
    }
    
    // Check if date is in the future
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0); // Reset time part
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time part
    
    // Convert both to timestamps for accurate comparison
    const selectedTimestamp = selectedDateObj.getTime();
    const todayTimestamp = today.getTime();
    
    if (selectedTimestamp > todayTimestamp) {
      setError('You cannot log miles for future dates');
      return;
    }
    
    // Add a validation check for maximum miles (changed from 125 to 100)
    if (Number(miles) > 100) {
      setError('The miles entered exceed the allowed daily limit');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Check if user already has an entry for the selected date
      const milesRef = collection(db, 'miles');
      const existingEntryQuery = query(
        milesRef,
        where('userId', '==', currentUser!.uid),
        where('date', '==', selectedDate)
      );
      const existingEntrySnapshot = await getDocs(existingEntryQuery);
      
      if (!existingEntrySnapshot.empty) {
        setError('You have already logged miles for this date');
        setLoading(false);
        return;
      }
      
      // Add miles to Firestore
      await addDoc(collection(db, 'miles'), {
        userId: currentUser!.uid,
        miles: Number(miles),
        date: selectedDate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Update user's own totalMiles field
      const userRef = doc(db, 'users', currentUser!.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentUserMiles = userData.totalMiles || 0;
        await updateDoc(userRef, {
          totalMiles: currentUserMiles + Number(miles)
        });
      }
      
      // Update user's team miles directly from the client
      if (userDetails?.teamId) {
        const teamRef = doc(db, 'teams', userDetails.teamId);
        await updateDoc(teamRef, {
          totalMiles: teamTotalMiles + Number(miles)
        });
        setTeamTotalMiles(prevMiles => prevMiles + Number(miles));
      }
      
      // Update province miles - Check if province document exists first
      if (userDetails?.province) {
        const provinceRef = doc(db, 'provinces', userDetails.province);
        const provinceDoc = await getDoc(provinceRef);
        
        if (provinceDoc.exists()) {
          // Document exists, update it
          await updateDoc(provinceRef, {
            totalMiles: provinceTotalMiles + Number(miles)
          });
        } else {
          // Document doesn't exist, create it with setDoc
          await setDoc(provinceRef, {
            totalMiles: Number(miles),
            participantCount: 1
          });
        }
        
        setProvinceTotalMiles(prevMiles => prevMiles + Number(miles));
      }
      
      // Update overall statistics
      const statsRef = doc(db, 'statistics', 'totalMiles');
      const statsDoc = await getDoc(statsRef);
      
      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          miles: (statsDoc.data().miles || 0) + Number(miles)
        });
      } else {
        await setDoc(statsRef, {
          miles: Number(miles)
        });
      }
      
      // Update user's total miles for UI
      setUserTotalMiles(prevMiles => prevMiles + Number(miles));
      
      // Update overall progress for UI
      setOverallProgress(prevProgress => prevProgress + Number(miles));
      
      // Show success message and reset form
      setSuccess(true);
      setMiles('');
      setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error logging miles:', error);
      setError('Failed to log miles. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Miles Logging Section */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-primary py-4 px-6">
                <h2 className="text-xl font-semibold text-white">Log Your Miles</h2>
              </div>
              
              <div className="p-6">
                {success && (
                  <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                    <p className="text-green-700">Miles logged successfully!</p>
                  </div>
                )}
                
                {error && (
                  <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="miles">
                      Miles Walked Today
                    </label>
                    <input
                      type="number"
                      id="miles"
                      name="miles"
                      min="0.1"
                      step="0.1"
                      value={miles}
                      onChange={(e) => setMiles(e.target.value)}
                      className="block w-full px-4 py-3 text-3xl font-bold border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="0.0"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="date">
                      Date
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      max={format(new Date(), 'yyyy-MM-dd')}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      You cannot select future dates
                    </p>
                  </div>
                  
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-secondary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    >
                      {loading ? 'Logging...' : 'Log Miles'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            
            {/* Recent Activity */}
            <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 py-3 px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              </div>
              
              <div className="p-6">
                {recentLogs.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No recent activity</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {recentLogs.map((log) => (
                      <li key={log.id} className="py-3">
                        <div className="flex justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {log.miles} miles
                            </p>
                            <p className="text-sm text-gray-500">
                              {new Date(log.date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleTimeString()}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          
          {/* Stats Section */}
          <div>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 py-3 px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Your Stats</h3>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-500">Your Total Miles</p>
                    <p className="text-3xl font-bold text-primary">{userTotalMiles.toFixed(1)}</p>
                  </div>
                  
                  {userDetails?.teamId && (
                    <div>
                      <p className="text-sm text-gray-500">Team: {teamName}</p>
                      <p className="text-2xl font-bold text-secondary">{teamTotalMiles.toFixed(1)} miles</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-gray-500">
                      {userDetails?.province ? provinceNames[userDetails.province] || userDetails.province : 'Your Province'} Total Miles
                    </p>
                    <p className="text-xl font-bold text-accent">{provinceTotalMiles.toFixed(1)}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Overall Progress */}
            <div className="mt-6 bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 py-3 px-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Overall Challenge Progress</h3>
              </div>
              
              <div className="p-6">
                <div className="mb-2 flex justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {(overallProgress / 10000).toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {overallProgress.toLocaleString()} / 1,000,000 miles
                  </span>
                </div>
                <div className="relative pt-1">
                  <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-gray-200">
                    <div 
                      style={{ width: `${Math.min(100, (overallProgress / 10000))}%` }} 
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary transition-all duration-500"
                    ></div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 text-center mt-2">
                  Join Canadians across the country as we work toward our collective goal!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilesLogging; 